import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/components/layout/app-shell'
import type { SidebarNavItem } from '@/components/layout/app-shell'
import { MobilePageHeader } from '@/components/mobile/page-header'
import { UserMobileShell } from '@/components/layout/mobile/user-mobile-shell'
import { useUserNavigation } from '@/features/user/shell/navigation'
import {
  portalAccountChangedEventName,
  readStoredPortalAccount,
  type PortalAccountChangedDetail,
} from '@/lib/auth/session-storage'
import { useMediaQuery } from '@/lib/use-media-query'

export function UserShellPage({
  activePath,
  children,
  navigation,
}: {
  activePath?: string
  children?: ReactNode
  navigation?: SidebarNavItem[]
}) {
  const [user, setUser] = useState(() => readStoredPortalAccount('user'))

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined
    }

    const syncUser = () => {
      setUser(readStoredPortalAccount('user'))
    }

    const handleAccountChange = (event: Event) => {
      const detail = (event as CustomEvent<PortalAccountChangedDetail | undefined>).detail

      if (!detail || detail.portal === 'user') {
        syncUser()
      }
    }

    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'user_data') {
        syncUser()
      }
    }

    window.addEventListener(portalAccountChangedEventName, handleAccountChange)
    window.addEventListener('storage', handleStorage)

    return () => {
      window.removeEventListener(portalAccountChangedEventName, handleAccountChange)
      window.removeEventListener('storage', handleStorage)
    }
  }, [])

  if (navigation) {
    return (
      <UserShellLayout activePath={activePath} navigation={navigation} user={user}>
        {children}
      </UserShellLayout>
    )
  }

  return (
    <UserShellWithDefaultNavigation activePath={activePath} user={user}>
      {children}
    </UserShellWithDefaultNavigation>
  )
}

function UserShellWithDefaultNavigation({
  activePath,
  children,
  user,
}: {
  activePath?: string
  children?: ReactNode
  user: ReturnType<typeof readStoredPortalAccount>
}) {
  const navigation = useUserNavigation()

  return (
    <UserShellLayout activePath={activePath} navigation={navigation} user={user}>
      {children}
    </UserShellLayout>
  )
}

function UserShellLayout({
  activePath,
  children,
  navigation,
  user,
}: {
  activePath?: string
  children?: ReactNode
  navigation: SidebarNavItem[]
  user: ReturnType<typeof readStoredPortalAccount>
}) {
  const { t } = useTranslation('shell')
  const isMobile = useMediaQuery('(max-width: 767px)')
  const workspace = (
    <section
      aria-label={t('workspace')}
      className="h-full overflow-hidden"
      data-testid="user-workspace"
    >
      {children}
    </section>
  )

  if (isMobile) {
    return (
      <UserMobileShell activePath={activePath} navigation={navigation} user={user}>
        {workspace}
      </UserMobileShell>
    )
  }

  return (
    <AppShell activePath={activePath} navigation={navigation} user={user}>
      {workspace}
    </AppShell>
  )
}

/**
 * placeholder 路由(/chat/new)的页面壳:桌面端只挂空工作区;
 * 移动端 shell 没有统一 header,需要自带菜单入口与页面标题。
 */
export function UserPlaceholderPage({ activePath }: { activePath: string }) {
  const { t } = useTranslation('shell')
  const isMobile = useMediaQuery('(max-width: 767px)')

  return (
    <UserShellPage activePath={activePath}>
      {isMobile ? <MobilePageHeader title={t('userNavigation.newChat')} /> : null}
    </UserShellPage>
  )
}
